import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { orpc } from '@/lib/orpc-client'
import { clearChatPool } from '@/lib/chat/chat-pool'

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

export type MePayload = Awaited<ReturnType<typeof orpc.me.get>>

interface AuthStateContextValue {
  status: AuthStatus
  me: MePayload | null
  refresh: () => Promise<MePayload | null>
  logout: () => Promise<void>
}

const AuthStateContext = createContext<AuthStateContextValue | undefined>(undefined)

export function AuthStateProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [me, setMe] = useState<MePayload | null>(null)

  const refresh = useCallback(async (): Promise<MePayload | null> => {
    try {
      const next = await orpc.me.get({})
      setMe(next)
      setStatus('authenticated')
      return next
    } catch {
      setMe(null)
      setStatus('unauthenticated')
      return null
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await orpc.auth.logout({})
    } finally {
      void clearChatPool()
      setMe(null)
      setStatus('unauthenticated')
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<AuthStateContextValue>(
    () => ({
      status,
      me,
      refresh,
      logout,
    }),
    [status, me, refresh, logout]
  )

  return <AuthStateContext.Provider value={value}>{children}</AuthStateContext.Provider>
}

export function useAuthState(): AuthStateContextValue {
  const context = useContext(AuthStateContext)
  if (!context) {
    throw new Error('useAuthState must be used within AuthStateProvider')
  }
  return context
}
